import React from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  Button, 
  Box, 
  Container,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';

import LogoutButton from '../auth/LogoutButton'

// If you're using Redux:
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../../slices/authSlice'; 

const sections = [
  { title: 'What is DivScore', href: '#whatIsDivscore' },
  { title: 'Features', href: '#featuresSection' },
  { title: 'Results', href: '#resultsSection' },
  { title: 'Pricing', href: '#pricingSection' }
];

const Navbar = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  // Pull from Redux store (or your AuthContext)
  const { isAuthenticated } = useSelector((state) => state.auth);

  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    // Dispatch logout action or call signOut, etc.
    dispatch(logout());
    handleMenuClose();
  };

  const scrollToSection = (sectionId) => {
    const isOnLandingPage = location.pathname === '/';
    const targetId = sectionId.replace('#', '');

    if (!isOnLandingPage) {
      // If not on landing page, navigate there first
      navigate('/', { state: { scrollTo: targetId } });
      return;
    }

    const element = document.getElementById(targetId);
    if (element) {
      const navbarHeight = 64;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    handleMenuClose();
  };

  return (
    <AppBar 
      position={isAuthenticated ? 'static' : 'fixed'}
      sx={{
        backgroundColor: 'white',
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
        borderRadius: 0,
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters>
          {/* LOGO / TITLE */}
          <Typography
            variant="h6"
            component={RouterLink}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            to="/"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              textDecoration: 'none',
              flexGrow: { xs: 1, md: 0 },
            }}
          >
            DivScore
          </Typography>

          {/* Conditionally render Desktop Menu Links */}
          {!isAuthenticated && (
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, ml: 4 }}>
              {sections.map((section) => (
                <Button
                  key={section.title}
                  onClick={() => scrollToSection(section.href)}
                  sx={{
                    color: 'text.primary',
                    mx: 1,
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  {section.title}
                </Button>
              ))}
              <Button
                component={RouterLink}
                to="/etfs"
                sx={{
                  color: 'text.primary',
                  mx: 1,
                  '&:hover': {
                    color: 'primary.main',
                  },
                }}
              >
                ETF Ranking
              </Button>
            </Box>
          )}

          {/* Mobile Menu Icon */}
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              onClick={handleMenuClick}
              color="primary"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              {/* Render sections only if not authenticated */}
              {!isAuthenticated && sections.map((section) => (
                <MenuItem 
                  key={section.title}
                  component="a"
                  href={section.href}
                  onClick={handleMenuClose}
                >
                  {section.title}
                </MenuItem>
              ))}
              {!isAuthenticated && (
                <MenuItem 
                  component={RouterLink}
                  to="/etf-ranking"
                  onClick={handleMenuClose}
                >
                  ETF Ranking
                </MenuItem>
              )}

              {/* Render authenticated user links */}
              {isAuthenticated && (
                <>
                  <MenuItem
                    component={RouterLink}
                    to="/dashboard"
                    onClick={handleMenuClose}
                  >
                    Dashboard
                  </MenuItem>
                  <MenuItem
                    component={RouterLink}
                    to="/portfolio"
                    onClick={handleMenuClose}
                  >
                    Portfolio
                  </MenuItem>
                  <MenuItem
                    component={RouterLink}
                    to="/account"
                    onClick={handleMenuClose}
                  >
                    Account
                  </MenuItem>
                  <MenuItem onClick={handleLogout}>Logout</MenuItem>
                </>
              )}
            </Menu>
          </Box>

          {/* Desktop Right Side Buttons */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2, marginLeft: 'auto' }}>
            {isAuthenticated ? (
              <>
                <Button 
                  component={RouterLink} 
                  to="/dashboard"
                  variant="text"
                  color="primary"
                >
                  Dashboard
                </Button>
                <Button 
                  component={RouterLink} 
                  to="/portfolio"
                  variant="text"
                  color="primary"
                >
                  Portfolio
                </Button>
                <Button 
                  component={RouterLink} 
                  to="/account"
                  variant="text"
                  color="primary"
                >
                  Account
                </Button>
                <LogoutButton />
              </>
            ) : (
              <>
                <Button 
                  component={RouterLink} 
                  to="/login" 
                  variant="outlined"
                  color="primary"
                >
                  Login
                </Button>
                <Button 
                  component={RouterLink} 
                  to="/signup/premium"
                  variant="contained"
                  color="primary"
                >
                  Get Started
                </Button>
              </>
            )}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar;
